import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const monthOptions = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const getWeekOptions = (year, month) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const weeks = [];
    for (let week = 1; week <= 5; week++) {
        const startDay = (week - 1) * 7 + 1;
        if (startDay > daysInMonth) break;
        const endDay = Math.min(week * 7, daysInMonth);
        const fmt = (d) =>
            new Date(year, month - 1, d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
        weeks.push({ weekNumber: week, label: `Week ${week}: ${fmt(startDay)} – ${fmt(endDay)}` });
    }
    return weeks;
};

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth(); // 0-based
const currentWeek = Math.ceil(new Date().getDate() / 7);

const OpsCertificates = () => {
    const { apiFetch } = useAuth();

    const [year, setYear] = useState(currentYear);
    const [month, setMonth] = useState(monthOptions[currentMonth]);
    const [weekNumber, setWeekNumber] = useState(currentWeek);
    const [properties, setProperties] = useState([]);
    const [propertyID, setPropertyID] = useState(null);
    const [fireCert, setFireCert] = useState(false);
    const [electricalCert, setElectricalCert] = useState(false);
    const [occupancyCert, setOccupancyCert] = useState(false);
    const [loading, setLoading] = useState(false);

    const yearOptions = [];
    for (let y = 2020; y <= currentYear; y++) yearOptions.push(y);

    const monthNumber = monthOptions.indexOf(month) + 1;
    const weekOptions = getWeekOptions(year, monthNumber);
    const period = year * 100 + monthNumber;

    // Load active properties on mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active !== false);
                    setProperties(active);
                    if (active.length > 0) setPropertyID(active[0].propertyID);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
            }
        };
        fetchProperties();
    }, [apiFetch]);

    // Reset week if it no longer exists in the selected month
    useEffect(() => {
        const valid = weekOptions.some(w => w.weekNumber === weekNumber);
        if (!valid) setWeekNumber(1);
    }, [year, month]);

    // Fetch existing certificate data when selection changes
    useEffect(() => {
        if (!propertyID) return;

        const fetchCertificates = async () => {
            setLoading(true);
            try {
                const res = await apiFetch(
                    `/api/OperationsCompliance/operations-certificates?period=${period}&propertyId=${propertyID}&weekNumber=${weekNumber}`
                );
                if (res.ok) {
                    const data = await res.json();
                    setFireCert(!!data.fireCertificate);
                    setElectricalCert(!!data.electricalCertificate);
                    setOccupancyCert(!!data.occupancyCertificate);
                } else {
                    setFireCert(false);
                    setElectricalCert(false);
                    setOccupancyCert(false);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setFireCert(false);
                setElectricalCert(false);
                setOccupancyCert(false);
            } finally {
                setLoading(false);
            }
        };

        fetchCertificates();
    }, [year, month, weekNumber, propertyID]);

    const isMonthDisabled = (idx) => year === currentYear && idx > currentMonth;

    const handleSave = async () => {
        if (!propertyID) {
            toast.error("Please select a property.");
            return;
        }
        setLoading(true);
        const payload = {
            period,
            weekNumber,
            propertyID,
            fireCertificate: fireCert,
            electricalCertificate: electricalCert,
            occupancyCertificate: occupancyCert
        };
        try {
            const res = await apiFetch('/api/OperationsCompliance/operations-certificates/upsert', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.created ? "Certificate created successfully!" : "Certificate updated successfully!");
            } else {
                toast.error("Failed to save certificate.");
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error("Failed to save certificate.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <h2>Operations Certificates</h2>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="year">Year:</label>
                <select id="year" value={year} onChange={e => setYear(Number(e.target.value))} disabled={loading}>
                    {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="month">Month:</label>
                <select id="month" value={month} onChange={e => setMonth(e.target.value)} disabled={loading}>
                    {monthOptions.map((m, idx) => (
                        <option key={m} value={m} disabled={isMonthDisabled(idx)}>{m}</option>
                    ))}
                </select>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="week">Week:</label>
                <select id="week" value={weekNumber} onChange={e => setWeekNumber(Number(e.target.value))} disabled={loading}>
                    {weekOptions.map(w => (
                        <option key={w.weekNumber} value={w.weekNumber}>{w.label}</option>
                    ))}
                </select>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="property">Property:</label>
                <select
                    id="property"
                    value={propertyID ?? ""}
                    onChange={e => setPropertyID(Number(e.target.value))}
                    disabled={loading}
                >
                    {properties.length > 0 ? (
                        properties.map(p => (
                            <option key={p.propertyID} value={p.propertyID}>{p.propertyName}</option>
                        ))
                    ) : (
                        <option value="">Loading properties...</option>
                    )}
                </select>
            </div>
            <div style={{ marginBottom: 8 }}>
                <label>
                    <input type="checkbox" checked={fireCert} onChange={e => setFireCert(e.target.checked)} disabled={loading} />
                    &nbsp;Fire Certificate
                </label>
            </div>
            <div style={{ marginBottom: 8 }}>
                <label>
                    <input type="checkbox" checked={electricalCert} onChange={e => setElectricalCert(e.target.checked)} disabled={loading} />
                    &nbsp;Electrical Certificate
                </label>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label>
                    <input type="checkbox" checked={occupancyCert} onChange={e => setOccupancyCert(e.target.checked)} disabled={loading} />
                    &nbsp;Occupancy Certificate
                </label>
            </div>
            <button type="button" style={{ width: "10%", padding: 8 }} onClick={handleSave} disabled={loading}>
                {loading ? "Saving..." : "Save"}
            </button>
        </div>
    );
};

export default OpsCertificates;